import type {
  StructuredEvaluationDetails,
  RuleEvaluationItem,
  KnowledgeCitationItem,
  EntityReferenceItem,
  StructuredEvaluationDetailsDimensionCritiques,
} from "../../gen/models";

const UUID_REGEX_STR = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";

/**
 * Parses legacy/unstructured reasoning text into a structured StructuredEvaluationDetails object.
 * Extracts rule evaluations, knowledge citations, dimension critiques, summary, and entity references.
 */
export function parseReasoningToDetails(
  reasoning?: string | null
): StructuredEvaluationDetails | null {
  if (!reasoning || !reasoning.trim()) {
    return null;
  }

  const text = reasoning.trim();
  const ruleEvals: RuleEvaluationItem[] = [];
  const knowledgeCitations: KnowledgeCitationItem[] = [];
  const dimensionCritiques: StructuredEvaluationDetailsDimensionCritiques = {};
  const entityRefs: EntityReferenceItem[] = [];
  const seenRuleIds = new Set<string>();
  const seenKnowledgeIds = new Set<string>();

  // 1. Extract Rule Violations / Adherences
  // Matches:
  // - "1. Under rule [UUID] (Title): Body..."
  // - "Under [rule:UUID] (Title): Body..."
  // - "1. rule [UUID]: Body..."
  // - "- Rule [UUID]: Body..."
  const rulePattern = new RegExp(
    `(?:(?:\\d+\\.|\\*|-)\\s*)?(?:Under\\s+)?(?:\\[rule:(${UUID_REGEX_STR})\\]|rule\\s*\\[(${UUID_REGEX_STR})\\])(?:\\s*\\(([^)]+)\\))?[:\\s,]*(.*?)(?=(?:\\n\\s*(?:\\d+\\.|\\*|-|\\b(?:Tool Efficiency|Factual Discrepancies|Rule Violations)\\b))|$)`,
    "gis"
  );

  let match: RegExpExecArray | null;
  while ((match = rulePattern.exec(text)) !== null) {
    const ruleId = match[1] || match[2];
    const ruleTitle = match[3]?.trim() || null;
    let explanation = match[4]?.trim() || "";

    if (!ruleId) continue;

    // Check for quote
    let violatedQuote: string | null = null;
    const quoteMatch = /(?:Quote|Claimed|Stated)[:\s]*"([^"]+)"/i.exec(explanation) ||
      /"([^"]{10,})"/i.exec(explanation);
    if (quoteMatch) {
      violatedQuote = quoteMatch[1].trim();
    }

    // Determine status & severity
    const lower = explanation.toLowerCase();
    const isAdhered = lower.includes("adhered") || lower.includes("complied") || lower.includes("followed");
    const status = isAdhered ? "adhered" : "violated";

    let severity = "major";
    if (lower.includes("critical") || lower.includes("hallucination") || lower.includes("strictly prohibited")) {
      severity = "critical";
    } else if (lower.includes("minor")) {
      severity = "minor";
    }

    ruleEvals.push({
      rule_id: ruleId,
      rule_title: ruleTitle,
      status,
      severity,
      explanation,
      violated_quote: violatedQuote,
    });

    if (!seenRuleIds.has(ruleId)) {
      seenRuleIds.add(ruleId);
      entityRefs.push({
        entity_type: "rule",
        entity_id: ruleId,
        title: ruleTitle || undefined,
        target_path: `/rules/${ruleId}`,
      });
    }
  }

  // 2. Extract Knowledge Discrepancies
  // Matches:
  // - "1. Contradiction against knowledge [UUID] (Title): Body..."
  // - "Contradiction against [knowledge:UUID] (Title): Body..."
  const knowledgePattern = new RegExp(
    `(?:(?:\\d+\\.|\\*|-)\\s*)?(?:Contradiction against\\s+)?(?:\\[knowledge:(${UUID_REGEX_STR})\\]|knowledge\\s*\\[(${UUID_REGEX_STR})\\])(?:\\s*\\(([^)]+)\\))?[:\\s,]*(.*?)(?=(?:\\n\\s*(?:\\d+\\.|\\*|-|\\b(?:Tool Efficiency|Factual Discrepancies|Rule Violations)\\b))|$)`,
    "gis"
  );

  while ((match = knowledgePattern.exec(text)) !== null) {
    const kId = match[1] || match[2];
    const kTitle = match[3]?.trim() || null;
    const body = match[4]?.trim() || "";

    if (!kId) continue;

    knowledgeCitations.push({
      knowledge_id: kId,
      knowledge_title: kTitle,
      status: "contradicted",
      explanation: body,
      claim: body,
    });

    if (!seenKnowledgeIds.has(kId)) {
      seenKnowledgeIds.add(kId);
      entityRefs.push({
        entity_type: "knowledge_item",
        entity_id: kId,
        title: kTitle || undefined,
        target_path: `/knowledge/${kId}`,
      });
    }
  }

  // 3. Extract Tool Efficiency Critique
  const tePattern = /(?:(?:\d+\.|\*|-)\s*)?Tool Efficiency(?:\s*\((?:Score\s*)?([0-9.]+)\)|\s+is graded\s+([0-9.]+)\s+because)?[:\s,]*(.*?)(?=(?:\n\s*(?:\d+\.|\*|-|\b(?:Factual Discrepancies|Rule Violations)\b))|$)/is;
  const teMatch = tePattern.exec(text);
  if (teMatch) {
    const rawScore = teMatch[1] || teMatch[2] || "1.0";
    const scoreVal = parseFloat(rawScore);
    const rationale = teMatch[3]?.trim() || "Evaluated tool efficiency.";
    dimensionCritiques["tool_efficiency"] = {
      score: isNaN(scoreVal) ? 1.0 : scoreVal,
      rationale,
    };
  }

  // 4. Extract Summary (introductory paragraph before rule sections)
  let summary: string | null = null;
  const introMatch = /^(.*?)(?=(?:Rule Violations:|(?:(?:\d+\.|\*|-)\s*)?(?:Under\s+)?(?:\[rule:|rule\s*\[)))/is.exec(text);
  if (introMatch && introMatch[1].trim()) {
    const cleanIntro = introMatch[1].trim().replace(/^Rule Violations:\s*/i, "");
    if (cleanIntro) {
      summary = cleanIntro;
    }
  }

  // Fallback summary if none found but rules exist
  if (!summary && ruleEvals.length > 0) {
    const vCount = ruleEvals.filter((r) => r.status === "violated").length;
    summary = `Audit completed: ${vCount} rule violation${vCount === 1 ? "" : "s"} identified.`;
  }

  // If no structured components could be extracted at all, return null
  if (
    ruleEvals.length === 0 &&
    knowledgeCitations.length === 0 &&
    Object.keys(dimensionCritiques).length === 0 &&
    !summary
  ) {
    return null;
  }

  return {
    summary: summary || text.slice(0, 200),
    rule_evaluations: ruleEvals,
    knowledge_citations: knowledgeCitations,
    dimension_critiques: dimensionCritiques,
    entity_references: entityRefs,
  };
}
